import React from "react";
import {
  ArrowRight, Play, CheckCircle2, XCircle, AlertTriangle, Quote,
  FileSpreadsheet, HelpCircle, Calculator, Clock, Search, Boxes, TrendingDown,
  TrendingUp, Coins, ShieldCheck, ClipboardCheck, Users,
  Truck, FlaskConical, Package, ScanBarcode, BarChart3,
  Timer, CalendarClock, Camera, Target, MonitorSmartphone,
  Factory, Droplets, BadgeCheck, Headset,
} from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { LandingNav } from "@/components/landing/LandingNav";
import { LogoMark } from "@/components/landing/Logo";
import { ProductMock } from "@/components/landing/ProductMock";
import { DemoModal, openDemoModal } from "@/components/landing/DemoModal";
import { getPublicPlans } from "@/lib/api";

const INDUSTRY_IMAGES = [
  "https://images.pexels.com/photos/821365/pexels-photo-821365.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1099680/pexels-photo-1099680.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/248412/pexels-photo-248412.jpeg?auto=compress&cs=tinysrgb&w=800",
];

/* ----------------------------- Bilingual content ----------------------------- */
const CONTENT = {
  ro: {
    hero: {
      title1: "Controlează fiecare litru de lapte din fabrica ta,",
      title2: "de la recepție până la produsul finit.",
      subtitle: "LactoPilot este software-ul dedicat industriei lactatelor care îți oferă control complet asupra producției, costurilor, stocurilor și trasabilității.",
      proof: [
        "Reduci pierderile de materie primă",
        "Calculezi automat costul real per kg",
        "Automatizezi HACCP și trasabilitatea",
        "Ești pregătit pentru controale",
        "Controlezi producția în timp real",
      ],
      ctaPrimary: "Programează o demonstrație gratuită",
      ctaSecondary: "Vezi cum funcționează",
    },
    pains: {
      title: "Îți sună cunoscut?",
      note: "LactoPilot elimină aceste probleme și îți oferă control complet asupra operațiunilor din fabrică.",
      items: [
        { icon: "FileSpreadsheet", text: "Folosești Excel-uri și registre pe hârtie" },
        { icon: "HelpCircle", text: "Nu știi exact randamentul fiecărei șarje" },
        { icon: "Calculator", text: "Costurile reale sunt calculate aproximativ" },
        { icon: "Clock", text: "Documentația HACCP consumă ore întregi" },
        { icon: "Search", text: "Trasabilitatea necesită căutări manuale" },
        { icon: "Boxes", text: "Stocurile nu sunt actualizate în timp real" },
        { icon: "TrendingDown", text: "Pierderile sunt observate prea târziu" },
      ],
    },
    results: {
      title: "Ce rezultate poți obține cu LactoPilot",
      items: [
        { icon: "TrendingUp", title: "Reducerea pierderilor", text: "Monitorizezi consumurile și randamentele în timp real și identifici rapid abaterile." },
        { icon: "Coins", title: "Costuri controlate", text: "Calculezi automat costul real per kilogram și știi exact profitabilitatea fiecărui produs." },
        { icon: "ShieldCheck", title: "Trasabilitate instantanee", text: "Identifici orice lot, orice ingredient, orice produs în câteva secunde." },
        { icon: "ClipboardCheck", title: "Audituri mai simple", text: "Toată documentația este organizată și disponibilă instant, pregătit pentru orice control." },
        { icon: "Users", title: "Productivitate crescută", text: "Mai puțin timp pentru administrație și mai mult timp pentru producție." },
      ],
    },
    flow: {
      title: "Un singur flux. Control total.",
      steps: [
        { icon: "Truck", color: "green", title: "1. Recepție lapte", items: ["Recepție automată", "Evidență furnizori", "Analize de calitate"] },
        { icon: "FlaskConical", color: "navy", title: "2. Producție", items: ["Rețete și șarje", "Consum materii prime", "Randamente"] },
        { icon: "Package", color: "purple", title: "3. Stocuri", items: ["Materii prime", "Ambalaje", "Produse finite"] },
        { icon: "ScanBarcode", color: "amber", title: "4. Trasabilitate", items: ["Loturi și subloturi", "Istoric complet", "Recall rapid"] },
        { icon: "BarChart3", color: "green", title: "5. Costuri", items: ["Costuri materii prime", "Costuri ambalaje", "Cost / kg produs"] },
        { icon: "Truck", color: "navy", title: "6. Livrare", items: ["Comenzi și livrări", "Trasabilitate clienți", "Rapoarte"] },
      ],
    },
    haccp: {
      title: "Economisește timp și bani cu documentația HACCP digitalizată",
      beforeTitle: "Înainte",
      before: ["Fișe completate manual", "Registre pe hârtie", "Verificări consumatoare de timp", "Documente pierdute sau incomplete", "Ore întregi pregătind controale"],
      afterTitle: "Cu LactoPilot",
      after: ["Înregistrări digitale automate", "Trasabilitate instantanee", "Arhivare centralizată", "Rapoarte generate automat", "Acces imediat la istoricul loturilor"],
      costTitle: "Costul ascuns al HACCP-ului manual",
      costIntro: "Pentru o fabrică medie:",
      costItems: [
        { icon: "Timer", value: "1,5 ore/zi", text: "completare și verificare registre" },
        { icon: "CalendarClock", value: "33 ore/lună", text: "pentru documente și verificări" },
        { icon: "Camera", value: "390 ore/an", text: "pierdere de timp administrativ" },
      ],
      costRate: "La un cost mediu de 35 lei/oră:",
      costTotal: "13.650 lei / an",
      costTotalNote: "doar pentru administrarea documentelor HACCP.",
      reduceText: "Reduci timpul dedicat HACCP și trasabilității cu până la",
      reducePct: "80%",
    },
    industries: {
      title: "Pentru cine este LactoPilot?",
      items: [
        { title: "Fabrici de brânzeturi", items: ["Telemea", "Mozzarella", "Cașcaval", "Feta", "Brânză proaspătă", "Alte tipuri de brânzeturi"] },
        { title: "Fabrici de produse fermentate", items: ["Iaurt", "Chefir", "Sana", "Lapte bătut", "Alte produse lactate"] },
        { title: "Cooperative și centre de colectare", items: ["Recepție lapte", "Analize", "Trasabilitate", "Management furnizori"] },
      ],
    },
    caseStudy: {
      title: "Studiu de caz",
      subtitle: "Cum a redus o fabrică pierderile cu 12%",
      beforeTitle: "Înainte",
      before: ["Excel și registre pe hârtie", "Trasabilitate lentă", "Pierderi neidentificate", "Costuri calculate aproximativ", "Mult timp pierdut cu documente"],
      afterTitle: "După implementarea LactoPilot",
      after: ["Toate datele într-un singur sistem", "Trasabilitate instantanee", "Pierderi reduse cu 12%", "Cost real per kg cunoscut", "Timp administrativ redus cu 70%"],
      quote: "LactoPilot ne-a oferit vizibilitate totală asupra producției. Acum știm exact ce producem, ce vindem și cât câștigăm.",
      author: "— Director General",
    },
    compare: {
      title: "Excel, ERP generic sau LactoPilot?",
      cols: ["Funcționalitate", "Excel", "ERP generic", "LactoPilot"],
      rows: [
        "Recepție lapte", "Analize lapte", "Randamente lactate", "Trasabilitate completă",
        "HACCP digital", "Cost / kg produs", "Management zer și produse secundare", "Adaptat industriei lactatelor",
      ],
      legend: { yes: "Da", partial: "Parțial", no: "Nu" },
    },
    audit: {
      title: "Solicită un audit gratuit al proceselor din fabrica ta",
      text: "Descoperă cât poți economisi prin digitalizarea producției, HACCP-ului și trasabilității cu LactoPilot.",
      cta: "Programează acum demonstrația gratuită",
      items: [
        { icon: "Search", text: "Analiza fluxului actual de producție" },
        { icon: "Target", text: "Identificarea punctelor cu pierderi" },
        { icon: "BarChart3", text: "Estimarea economiilor posibile" },
        { icon: "MonitorSmartphone", text: "Demonstrație personalizată" },
      ],
    },
    stats: [
      { icon: "Factory", value: "50+", text: "fabrici de lactate folosesc LactoPilot" },
      { icon: "Droplets", value: "500.000+", text: "litri de lapte monitorizați zilnic în platformă" },
      { icon: "BadgeCheck", value: "100%", text: "conformitate și trasabilitate în câteva secunde" },
      { icon: "Headset", value: "Suport local", text: "echipă dedicată în România, alături de tine" },
    ],
    pricing: {
      title: "Prețuri simple, transparente",
      subtitle: "Alege planul potrivit pentru fabrica ta. Fără costuri ascunse, poți schimba planul oricând.",
      badge: "Cel mai popular",
      perMonth: "/lună",
      free: "Gratuit",
      cta: "Începe acum",
      ctaPopular: "Programează un demo",
      vat: "Prețurile nu includ TVA. Facturare lunară.",
    },
    footerTag: "Control total în industria lactatelor.",
  },
  en: {
    hero: {
      title1: "Control every litre of milk in your factory,",
      title2: "from intake to the finished product.",
      subtitle: "LactoPilot is the dedicated dairy-industry software giving you complete control over production, costs, stock and traceability.",
      proof: ["Cut raw-material losses", "Auto-calculate real cost per kg", "Automate HACCP & traceability", "Be audit-ready", "Control production in real time"],
      ctaPrimary: "Book a free demo",
      ctaSecondary: "See how it works",
    },
    pains: {
      title: "Sound familiar?",
      note: "LactoPilot removes these problems and gives you complete control over your factory operations.",
      items: [
        { icon: "FileSpreadsheet", text: "You rely on spreadsheets and paper logs" },
        { icon: "HelpCircle", text: "You don't know each batch's exact yield" },
        { icon: "Calculator", text: "Real costs are only estimated" },
        { icon: "Clock", text: "HACCP paperwork takes hours" },
        { icon: "Search", text: "Traceability needs manual searching" },
        { icon: "Boxes", text: "Stock isn't updated in real time" },
        { icon: "TrendingDown", text: "Losses are noticed too late" },
      ],
    },
    results: {
      title: "What you can achieve with LactoPilot",
      items: [
        { icon: "TrendingUp", title: "Fewer losses", text: "Monitor consumption and yields in real time and spot deviations fast." },
        { icon: "Coins", title: "Controlled costs", text: "Auto-calculate real cost per kg and know each product's profitability." },
        { icon: "ShieldCheck", title: "Instant traceability", text: "Trace any lot, ingredient or product in seconds." },
        { icon: "ClipboardCheck", title: "Easier audits", text: "All documentation organised and available instantly, ready for any inspection." },
        { icon: "Users", title: "Higher productivity", text: "Less time on admin, more time on production." },
      ],
    },
    flow: {
      title: "One flow. Total control.",
      steps: [
        { icon: "Truck", color: "green", title: "1. Milk intake", items: ["Automatic intake", "Supplier records", "Quality analysis"] },
        { icon: "FlaskConical", color: "navy", title: "2. Production", items: ["Recipes & batches", "Raw-material use", "Yields"] },
        { icon: "Package", color: "purple", title: "3. Stock", items: ["Raw materials", "Packaging", "Finished goods"] },
        { icon: "ScanBarcode", color: "amber", title: "4. Traceability", items: ["Lots & sub-lots", "Full history", "Fast recall"] },
        { icon: "BarChart3", color: "green", title: "5. Costs", items: ["Material costs", "Packaging costs", "Cost / kg"] },
        { icon: "Truck", color: "navy", title: "6. Delivery", items: ["Orders & deliveries", "Client traceability", "Reports"] },
      ],
    },
    haccp: {
      title: "Save time and money with digital HACCP documentation",
      beforeTitle: "Before",
      before: ["Manually filled forms", "Paper logs", "Time-consuming checks", "Lost or incomplete documents", "Hours preparing for inspections"],
      afterTitle: "With LactoPilot",
      after: ["Automatic digital records", "Instant traceability", "Centralised archiving", "Auto-generated reports", "Instant access to lot history"],
      costTitle: "The hidden cost of manual HACCP",
      costIntro: "For an average factory:",
      costItems: [
        { icon: "Timer", value: "1.5 h/day", text: "filling & checking logs" },
        { icon: "CalendarClock", value: "33 h/month", text: "for documents & checks" },
        { icon: "Camera", value: "390 h/year", text: "administrative time lost" },
      ],
      costRate: "At an average cost of 35 lei/hour:",
      costTotal: "13,650 lei / year",
      costTotalNote: "just to manage HACCP documents.",
      reduceText: "Cut time spent on HACCP and traceability by up to",
      reducePct: "80%",
    },
    industries: {
      title: "Who is LactoPilot for?",
      items: [
        { title: "Cheese factories", items: ["Telemea", "Mozzarella", "Cașcaval", "Feta", "Fresh cheese", "Other cheeses"] },
        { title: "Fermented product makers", items: ["Yogurt", "Kefir", "Sana", "Buttermilk", "Other dairy"] },
        { title: "Co-ops & collection centres", items: ["Milk intake", "Analysis", "Traceability", "Supplier management"] },
      ],
    },
    caseStudy: {
      title: "Case study",
      subtitle: "How one factory cut losses by 12%",
      beforeTitle: "Before",
      before: ["Excel and paper logs", "Slow traceability", "Unidentified losses", "Roughly estimated costs", "Lots of time lost on documents"],
      afterTitle: "After LactoPilot",
      after: ["All data in one system", "Instant traceability", "Losses cut by 12%", "Real cost per kg known", "Admin time cut by 70%"],
      quote: "LactoPilot gave us total visibility over production. Now we know exactly what we make, what we sell and what we earn.",
      author: "— General Manager",
    },
    compare: {
      title: "Excel, generic ERP or LactoPilot?",
      cols: ["Feature", "Excel", "Generic ERP", "LactoPilot"],
      rows: ["Milk intake", "Milk analysis", "Dairy yields", "Full traceability", "Digital HACCP", "Cost / kg", "Whey & by-product management", "Built for dairy"],
      legend: { yes: "Yes", partial: "Partial", no: "No" },
    },
    audit: {
      title: "Request a free audit of your factory processes",
      text: "Discover how much you can save by digitising production, HACCP and traceability with LactoPilot.",
      cta: "Book your free demo now",
      items: [
        { icon: "Search", text: "Analysis of your current production flow" },
        { icon: "Target", text: "Spotting loss points" },
        { icon: "BarChart3", text: "Estimating possible savings" },
        { icon: "MonitorSmartphone", text: "Personalised demo" },
      ],
    },
    stats: [
      { icon: "Factory", value: "50+", text: "dairy factories use LactoPilot" },
      { icon: "Droplets", value: "500,000+", text: "litres of milk monitored daily" },
      { icon: "BadgeCheck", value: "100%", text: "compliance & traceability in seconds" },
      { icon: "Headset", value: "Local support", text: "a dedicated team in Romania, by your side" },
    ],
    pricing: {
      title: "Simple, transparent pricing",
      subtitle: "Pick the plan that fits your factory. No hidden fees — change your plan anytime.",
      badge: "Most popular",
      perMonth: "/month",
      free: "Free",
      cta: "Get started",
      ctaPopular: "Schedule a demo",
      vat: "Prices exclude VAT. Billed monthly.",
    },
    footerTag: "Total control for the dairy industry.",
  },
};

const ICONS = {
  FileSpreadsheet, HelpCircle, Calculator, Clock, Search, Boxes, TrendingDown,
  TrendingUp, Coins, ShieldCheck, ClipboardCheck, Users,
  Truck, FlaskConical, Package, ScanBarcode, BarChart3,
  Timer, CalendarClock, Camera, Target, MonitorSmartphone,
  Factory, Droplets, BadgeCheck, Headset,
};

const STEP_TONE = {
  green: "bg-[hsl(var(--lp-green)/0.12)] text-[hsl(var(--lp-green-deep))]",
  navy: "bg-[hsl(var(--lp-navy)/0.10)] text-[hsl(var(--lp-navy))]",
  purple: "bg-[hsl(280_55%_62%/0.14)] text-[hsl(280_45%_48%)]",
  amber: "bg-[hsl(38_92%_55%/0.16)] text-[hsl(32_85%_45%)]",
};

const navyBtn = "inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[hsl(var(--lp-navy))] px-6 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-[hsl(var(--lp-navy-deep))] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lp-navy))] focus-visible:ring-offset-2";
const outlineBtn = "inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-[hsl(var(--lp-navy)/0.25)] bg-[hsl(var(--lp-bg))] px-6 text-sm font-semibold text-[hsl(var(--lp-navy))] transition-colors duration-200 hover:bg-[hsl(var(--lp-navy)/0.06)]";

const SectionTitle = ({ children, light }) => (
  <h2 className={`text-center text-2xl font-bold tracking-tight sm:text-3xl lg:text-[2.1rem] ${light ? "text-white" : "text-[hsl(var(--lp-ink))]"}`}>
    {children}
  </h2>
);

export default function LandingPage() {
  const { lang } = useT();
  const C = CONTENT[lang] || CONTENT.ro;
  const [plans, setPlans] = React.useState([]);

  React.useEffect(() => {
    let active = true;
    getPublicPlans()
      .then((data) => { if (active) setPlans(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setPlans([]); });
    return () => { active = false; };
  }, []);

  const fmtPrice = (n) => new Intl.NumberFormat(lang === "ro" ? "ro-RO" : "en-US").format(Number(n) || 0);
  const splitFeatures = (s) => String(s || "").split("·").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="lp-scope min-h-screen">
      <DemoModal />
      <LandingNav />

      <main>
        {/* ---------------- Hero ---------------- */}
        <section className="lp-hero relative overflow-hidden">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:py-20">
            <div className="lg:col-span-6">
              <h1 className="text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl lg:text-[2.9rem]">
                <span className="text-[hsl(var(--lp-ink))]">{C.hero.title1}</span>{" "}
                <span className="text-[hsl(var(--lp-navy))]">{C.hero.title2}</span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-[hsl(var(--lp-body))]">{C.hero.subtitle}</p>
              <div className="mt-6 grid max-w-xl grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                {C.hero.proof.map((p) => (
                  <span key={p} className="inline-flex items-start gap-1.5 text-sm font-medium text-[hsl(var(--lp-body))]">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--lp-green))]" />{p}
                  </span>
                ))}
              </div>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={openDemoModal} className={navyBtn} data-testid="landing-hero-primary-cta">
                  {C.hero.ctaPrimary} <ArrowRight className="h-4 w-4" />
                </button>
                <a href="#functionalities" className={outlineBtn} data-testid="landing-hero-secondary-cta">
                  <Play className="h-4 w-4 text-[hsl(var(--lp-green))]" /> {C.hero.ctaSecondary}
                </a>
              </div>
            </div>
            <div className="lg:col-span-6">
              <ProductMock />
            </div>
          </div>
        </section>

        {/* ---------------- Pain points ---------------- */}
        <section className="lp-grid border-y border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-soft))]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20" data-testid="landing-pains-section">
            <SectionTitle>{C.pains.title}</SectionTitle>
            <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-4 lg:grid-cols-7">
              {C.pains.items.map((p) => {
                const Icon = ICONS[p.icon];
                return (
                  <div key={p.text} className="flex flex-col items-center text-center">
                    <span className="grid h-14 w-14 place-items-center rounded-full bg-[hsl(var(--lp-bg))] text-[hsl(var(--lp-navy))] shadow-[0_8px_20px_-10px_hsl(var(--lp-ink)/0.35)] ring-1 ring-[hsl(var(--lp-border))]">
                      <Icon className="h-6 w-6" />
                    </span>
                    <p className="mt-3 text-xs leading-relaxed text-[hsl(var(--lp-body))]">{p.text}</p>
                  </div>
                );
              })}
            </div>
            <p className="mx-auto mt-10 max-w-3xl text-center text-sm text-[hsl(var(--lp-body))]">{C.pains.note}</p>
          </div>
        </section>

        {/* ---------------- Results / benefits ---------------- */}
        <section id="results" className="scroll-mt-20 border-b border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-tint))]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20" data-testid="landing-results-section">
            <SectionTitle>{C.results.title}</SectionTitle>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {C.results.items.map((r) => {
                const Icon = ICONS[r.icon];
                return (
                  <div key={r.title} className="lp-card rounded-2xl p-5 transition-shadow duration-200 hover:shadow-[0_22px_50px_-24px_hsl(var(--lp-ink)/0.35)]">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-[hsl(var(--lp-green)/0.12)] text-[hsl(var(--lp-green-deep))]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 text-base font-bold text-[hsl(var(--lp-ink))]">{r.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--lp-body))]">{r.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ---------------- Flow ---------------- */}
        <section id="functionalities" className="lp-grid scroll-mt-20 border-b border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-soft))]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20" data-testid="landing-flow-section">
            <SectionTitle>{C.flow.title}</SectionTitle>
            <div className="mt-12 grid grid-cols-1 gap-x-4 gap-y-10 sm:grid-cols-2 lg:grid-cols-6">
              {C.flow.steps.map((s, idx) => {
                const Icon = ICONS[s.icon];
                return (
                  <div key={s.title} className="relative flex flex-col items-center text-center">
                    <span className={`grid h-14 w-14 place-items-center rounded-full ${STEP_TONE[s.color]}`}>
                      <Icon className="h-6 w-6" />
                    </span>
                    {idx < C.flow.steps.length - 1 && (
                      <ArrowRight className="absolute -right-2 top-4 hidden h-5 w-5 text-[hsl(var(--lp-navy)/0.35)] lg:block" />
                    )}
                    <h3 className="mt-3 text-sm font-bold text-[hsl(var(--lp-ink))]">{s.title}</h3>
                    <ul className="mt-2 space-y-1">
                      {s.items.map((it) => (
                        <li key={it} className="text-xs text-[hsl(var(--lp-body))]">{it}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ---------------- HACCP savings ---------------- */}
        <section className="border-b border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-tint))]">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-20" data-testid="landing-haccp-section">
            <div>
              <h2 className="text-2xl font-bold leading-tight tracking-tight text-[hsl(var(--lp-ink))] sm:text-[1.8rem]">{C.haccp.title}</h2>
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <div className="text-sm font-bold text-[hsl(var(--lp-navy))]">{C.haccp.beforeTitle}</div>
                  <ul className="mt-3 space-y-2">
                    {C.haccp.before.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-[hsl(var(--lp-body))]"><XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(0_72%_55%)]" />{b}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-sm font-bold text-[hsl(var(--lp-green-deep))]">{C.haccp.afterTitle}</div>
                  <ul className="mt-3 space-y-2">
                    {C.haccp.after.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-[hsl(var(--lp-body))]"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--lp-green))]" />{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="lp-card rounded-2xl p-6">
              <h3 className="text-lg font-bold text-[hsl(var(--lp-ink))]">{C.haccp.costTitle}</h3>
              <p className="mt-1 text-sm text-[hsl(var(--lp-body))]">{C.haccp.costIntro}</p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {C.haccp.costItems.map((ci) => {
                  const Icon = ICONS[ci.icon];
                  return (
                    <div key={ci.value} className="flex flex-col items-center text-center">
                      <span className="grid h-10 w-10 place-items-center rounded-full bg-[hsl(var(--lp-navy)/0.07)] text-[hsl(var(--lp-navy))]"><Icon className="h-5 w-5" /></span>
                      <div className="mt-2 text-sm font-bold text-[hsl(var(--lp-ink))]">{ci.value}</div>
                      <div className="text-[11px] leading-tight text-[hsl(var(--lp-body))]">{ci.text}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 flex flex-col items-start gap-4 border-t border-[hsl(var(--lp-border))] pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs text-[hsl(var(--lp-body))]">{C.haccp.costRate}</div>
                  <div className="mt-1 text-2xl font-extrabold text-[hsl(var(--lp-green-deep))]">{C.haccp.costTotal}</div>
                  <div className="text-[11px] text-[hsl(var(--lp-body))]">{C.haccp.costTotalNote}</div>
                </div>
                <div className="rounded-xl bg-[hsl(var(--lp-green)/0.10)] px-4 py-3 text-center">
                  <div className="max-w-[10rem] text-[11px] leading-tight text-[hsl(var(--lp-green-deep))]">{C.haccp.reduceText}</div>
                  <div className="text-3xl font-extrabold text-[hsl(var(--lp-green-deep))]">{C.haccp.reducePct}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- Industries ---------------- */}
        <section id="industries" className="lp-grid scroll-mt-20 border-b border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-soft))]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20" data-testid="landing-industries-section">
            <SectionTitle>{C.industries.title}</SectionTitle>
            <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {C.industries.items.map((ind, i) => (
                <div key={ind.title} className="lp-card overflow-hidden rounded-2xl transition-shadow duration-200 hover:shadow-[0_24px_55px_-26px_hsl(var(--lp-ink)/0.45)]">
                  <img src={INDUSTRY_IMAGES[i]} alt={ind.title} loading="lazy" className="h-40 w-full object-cover" />
                  <div className="p-5">
                    <h3 className="text-base font-bold text-[hsl(var(--lp-ink))]">{ind.title}</h3>
                    <ul className="mt-3 space-y-1.5">
                      {ind.items.map((it) => (
                        <li key={it} className="flex items-center gap-2 text-sm text-[hsl(var(--lp-body))]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--lp-green))]" />{it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- Case study + comparison ---------------- */}
        <section id="resources" className="scroll-mt-20 border-b border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-tint))]">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-20" data-testid="landing-resources-section">
            {/* Case study */}
            <div className="lp-card rounded-2xl p-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-[hsl(var(--lp-ink))]">{C.caseStudy.title}</h2>
                <p className="mt-1 text-sm font-semibold text-[hsl(var(--lp-navy))]">{C.caseStudy.subtitle}</p>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <div className="text-sm font-bold text-[hsl(var(--lp-navy))]">{C.caseStudy.beforeTitle}</div>
                  <ul className="mt-2 space-y-1.5">
                    {C.caseStudy.before.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-xs text-[hsl(var(--lp-body))]"><XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(0_72%_55%)]" />{b}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-sm font-bold text-[hsl(var(--lp-green-deep))]">{C.caseStudy.afterTitle}</div>
                  <ul className="mt-2 space-y-1.5">
                    {C.caseStudy.after.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-xs text-[hsl(var(--lp-body))]"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--lp-green))]" />{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <blockquote className="mt-5 border-t border-[hsl(var(--lp-border))] pt-4">
                <Quote className="h-5 w-5 text-[hsl(var(--lp-navy)/0.4)]" />
                <p className="mt-1 text-sm italic text-[hsl(var(--lp-body))]">{C.caseStudy.quote}</p>
                <footer className="mt-2 text-xs font-semibold text-[hsl(var(--lp-ink))]">{C.caseStudy.author}</footer>
              </blockquote>
            </div>

            {/* Comparison table */}
            <div className="lp-card rounded-2xl p-6">
              <h2 className="text-xl font-bold text-[hsl(var(--lp-ink))]">{C.compare.title}</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[hsl(var(--lp-border))] text-left">
                      <th className="py-2 pr-2 font-semibold text-[hsl(var(--lp-ink))]">{C.compare.cols[0]}</th>
                      <th className="px-2 py-2 text-center font-semibold text-[hsl(var(--lp-body))]">{C.compare.cols[1]}</th>
                      <th className="px-2 py-2 text-center font-semibold text-[hsl(var(--lp-body))]">{C.compare.cols[2]}</th>
                      <th className="px-2 py-2 text-center font-semibold text-[hsl(var(--lp-navy))]">{C.compare.cols[3]}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {C.compare.rows.map((row) => (
                      <tr key={row} className="border-b border-[hsl(var(--lp-border)/0.6)]">
                        <td className="py-2 pr-2 text-[hsl(var(--lp-body))]">{row}</td>
                        <td className="px-2 py-2 text-center"><XCircle className="mx-auto h-4 w-4 text-[hsl(0_72%_55%)]" /></td>
                        <td className="px-2 py-2 text-center"><AlertTriangle className="mx-auto h-4 w-4 text-[hsl(38_92%_50%)]" /></td>
                        <td className="px-2 py-2 text-center"><CheckCircle2 className="mx-auto h-4 w-4 text-[hsl(var(--lp-green))]" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-[hsl(var(--lp-body))]">
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--lp-green))]" />{C.compare.legend.yes}</span>
                <span className="inline-flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-[hsl(38_92%_50%)]" />{C.compare.legend.partial}</span>
                <span className="inline-flex items-center gap-1"><XCircle className="h-4 w-4 text-[hsl(0_72%_55%)]" />{C.compare.legend.no}</span>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- Pricing ---------------- */}
        <section id="pricing" className="scroll-mt-20 border-b border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-soft))]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20" data-testid="landing-pricing-section">
            <SectionTitle>{C.pricing.title}</SectionTitle>
            <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-[hsl(var(--lp-body))]">
              {C.pricing.subtitle}
            </p>

            {plans.length > 0 && (
              <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {plans.map((p) => {
                  const popular = p.code === "pro";
                  const isFree = Number(p.price_ron) === 0 && Number(p.price_eur) === 0;
                  const feats = splitFeatures(p.features);
                  return (
                    <div
                      key={p.id}
                      data-testid={`landing-plan-card-${p.code}`}
                      className={`relative flex flex-col rounded-2xl border bg-[hsl(var(--lp-bg))] p-6 transition-transform duration-200 hover:-translate-y-1 ${
                        popular
                          ? "border-[hsl(var(--lp-navy))] shadow-[0_20px_45px_-20px_hsl(var(--lp-navy)/0.5)] ring-1 ring-[hsl(var(--lp-navy)/0.35)] lg:scale-[1.03]"
                          : "border-[hsl(var(--lp-border))] shadow-[0_10px_30px_-18px_hsl(var(--lp-ink)/0.35)]"
                      }`}
                    >
                      {popular && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[hsl(var(--lp-navy))] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white" data-testid={`landing-plan-badge-${p.code}`}>
                          {C.pricing.badge}
                        </span>
                      )}
                      <h3 className="text-lg font-bold text-[hsl(var(--lp-ink))]" data-testid={`landing-plan-name-${p.code}`}>{p.name}</h3>

                      <div className="mt-4 flex items-end gap-1">
                        {isFree ? (
                          <span className="text-3xl font-extrabold text-[hsl(var(--lp-ink))]" data-testid={`landing-plan-price-${p.code}`}>{C.pricing.free}</span>
                        ) : (
                          <>
                            <span className="text-3xl font-extrabold text-[hsl(var(--lp-ink))]" data-testid={`landing-plan-price-${p.code}`}>
                              {fmtPrice(p.price_ron)} <span className="text-lg font-bold">RON</span>
                            </span>
                            <span className="pb-1 text-sm font-medium text-[hsl(var(--lp-body))]">{C.pricing.perMonth}</span>
                          </>
                        )}
                      </div>
                      {!isFree && Number(p.price_eur) > 0 && (
                        <div className="mt-1 text-xs text-[hsl(var(--lp-body))]">≈ {fmtPrice(p.price_eur)} EUR {C.pricing.perMonth}</div>
                      )}

                      <ul className="mt-6 flex flex-1 flex-col gap-3">
                        {feats.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm text-[hsl(var(--lp-body))]">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--lp-green-deep))]" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        type="button"
                        onClick={openDemoModal}
                        className={`mt-8 w-full ${popular ? navyBtn : outlineBtn}`}
                        data-testid={`landing-plan-cta-${p.code}`}
                      >
                        {popular ? C.pricing.ctaPopular : C.pricing.cta}
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="mt-8 text-center text-xs text-[hsl(var(--lp-body))]">{C.pricing.vat}</p>
          </div>
        </section>

        {/* ---------------- Audit CTA band ---------------- */}
        <section id="about" className="lp-band-navy scroll-mt-20">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="landing-audit-section">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div>
                <h2 className="text-2xl font-bold leading-tight tracking-tight text-white sm:text-[1.8rem]">{C.audit.title}</h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-white/80">{C.audit.text}</p>
                <button type="button" onClick={openDemoModal} className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-white px-6 text-sm font-semibold text-[hsl(var(--lp-navy))] transition-transform duration-200 hover:bg-white/90 active:scale-[0.98]" data-testid="landing-audit-cta">
                  {C.audit.cta} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-4 lg:grid-cols-2">
                {C.audit.items.map((a) => {
                  const Icon = ICONS[a.icon];
                  return (
                    <div key={a.text} className="flex flex-col items-center text-center lg:flex-row lg:items-center lg:gap-3 lg:text-left">
                      <span className="grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white"><Icon className="h-5 w-5" /></span>
                      <p className="mt-2 text-xs leading-snug text-white/85 lg:mt-0">{a.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- Stats ---------------- */}
        <section className="border-b border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-soft))]">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="landing-stats-section">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {C.stats.map((s) => {
                const Icon = ICONS[s.icon];
                return (
                  <div key={s.text} className="flex items-center gap-4">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[hsl(var(--lp-navy)/0.07)] text-[hsl(var(--lp-navy))]"><Icon className="h-6 w-6" /></span>
                    <div>
                      <div className="text-xl font-extrabold text-[hsl(var(--lp-green-deep))]">{s.value}</div>
                      <div className="text-xs leading-snug text-[hsl(var(--lp-body))]">{s.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      {/* ---------------- Footer ---------------- */}
      <footer className="border-t border-[hsl(var(--lp-border))] bg-[hsl(var(--lp-bg-soft))]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <LogoMark className="h-8 w-8" />
            <div>
              <div className="text-sm font-bold text-[hsl(var(--lp-ink))]">LactoPilot</div>
              <div className="text-[11px] text-[hsl(var(--lp-body))]">{C.footerTag}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button type="button" onClick={openDemoModal} className={navyBtn} data-testid="landing-footer-cta">
              {C.hero.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="border-t border-[hsl(var(--lp-border))] py-4 text-center text-xs text-[hsl(var(--lp-body))]">
          © {new Date().getFullYear()} LactoPilot
        </div>
      </footer>
    </div>
  );
}
